import { Router } from "@remix-run/router/dist/router";
import * as Sentry from "@sentry/react";

import { loginPathFor, PUBLIC_ROUTES } from "@/utils/loginRedirect";
import { getToken, removeToken, setToken } from "@/utils/tokenManager";

import { isAndroidMobileApp } from "@/utils/platform";

import { createSpanAttributes, sendToSentry, updateSpanWithResponse } from "@/utils/sentry";

import { fetchWithRetry, isClientError } from "./fetchWithRetry";
import {
  ApiResponse,
  AuthResponse,
  CustomRequestInit,
  FetchWithAuthOptions,
  getErrorMessage,
  getNetworkErrorMessage,
} from "./types";

export const apiBase =
  import.meta.env.MODE === "production"
    ? import.meta.env.VITE_API_URL
    : isAndroidMobileApp()
      ? "http://10.0.2.2:24891"
      : import.meta.env.VITE_DEV_URL;

export let routerInstance: Router | null = null;
export const setRouterInstance = (router: Router) => {
  routerInstance = router;
};

/**
 * Outcome of POST /auth/refresh:
 *   ok          new access token stored
 *   rejected    401 bad token, 404 deleted user, 403: log out
 *   unavailable 5xx, network, anything else: keep the session, report a network error
 */
export type RefreshResult = "ok" | "rejected" | "unavailable";

const REJECTED_REFRESH_STATUSES = new Set([401, 403, 404]);

const refreshResultForStatus = (status: number): Exclude<RefreshResult, "ok"> =>
  REJECTED_REFRESH_STATUSES.has(status) ? "rejected" : "unavailable";

let refreshTokenPromise: Promise<RefreshResult> | null = null;

const AUTH_FAILED_MESSAGE = "Authentication failed after token refresh attempt";
const REFRESH_UNAVAILABLE_MESSAGE = "Token refresh unavailable";

// Expected session outcomes, not bugs: keep them out of Sentry.
const isSessionOutcome = (error: Error): boolean =>
  error.message === AUTH_FAILED_MESSAGE || error.message === REFRESH_UNAVAILABLE_MESSAGE;

const REDACTED_VALUE = "[REDACTED]";

const isSensitiveRequestField = (fieldName: string): boolean => {
  const normalizedName = fieldName.toLowerCase();
  return normalizedName.includes("password") || normalizedName === "clientsecret" || normalizedName === "client_secret";
};

const redactRequestValue = (value: unknown): unknown => {
  if (Array.isArray(value)) {
    return value.map(redactRequestValue);
  }

  if (value && typeof value === "object") {
    return Object.fromEntries(
      Object.entries(value).map(([fieldName, fieldValue]) => [
        fieldName,
        isSensitiveRequestField(fieldName) ? REDACTED_VALUE : redactRequestValue(fieldValue),
      ]),
    );
  }

  return value;
};

export const redactRequestBodyForSentry = (body: BodyInit | null | undefined): string | null | undefined => {
  if (body === null || body === undefined) {
    return body;
  }

  if (body instanceof FormData) {
    return "FormData";
  }

  if (typeof body !== "string") {
    return "[non-text request body]";
  }

  try {
    return JSON.stringify(redactRequestValue(JSON.parse(body)));
  } catch {
    return /password|clientsecret|client_secret/i.test(body) ? REDACTED_VALUE : body;
  }
};

const redactHeadersForSentry = (headers: HeadersInit | undefined): Record<string, string> | undefined => {
  if (!headers) {
    return undefined;
  }

  const redactedHeaders: Record<string, string> = {};
  new Headers(headers).forEach((value, fieldName) => {
    redactedHeaders[fieldName] = fieldName.toLowerCase() === "authorization" ? REDACTED_VALUE : value;
  });
  return redactedHeaders;
};

const redactOptionsForSentry = (options: CustomRequestInit): CustomRequestInit => ({
  ...options,
  headers: redactHeadersForSentry(options.headers),
  body: redactRequestBodyForSentry(options.body),
});

const protectSensitiveJsonRequestBody = (body: BodyInit | null | undefined): BodyInit | null | undefined => {
  if (typeof body !== "string" || redactRequestBodyForSentry(body) === body) {
    return body;
  }

  return new Blob([body], { type: "application/json" });
};

const parseResponseBody = async (response: Response): Promise<any> => {
  try {
    return await response.json();
  } catch {
    try {
      return await response.text();
    } catch {
      return null;
    }
  }
};

export const fetchUsingApi = async <Type>(
  url: string,
  method: string,
  resultProcessingFunction: (result: any) => Type,
  body?: BodyInit | null | undefined,
): Promise<ApiResponse<Type>> => {
  let response: Response | undefined;
  let responseBody: any;

  try {
    const headers: Record<string, string> = {};

    if (!(body instanceof FormData)) {
      headers["Content-Type"] = "application/json";
    }

    const options: CustomRequestInit = {
      method,
      cache: "no-store",
      headers,
      body,
    };

    response = await fetchWithAuth(url, options);

    if (response.ok) {
      // Check if the response has content
      const contentType = response.headers.get("content-type");
      let result;

      if (contentType?.includes("application/json")) {
        const responseBody = await response.json();
        result = resultProcessingFunction(responseBody);
      } else {
        // Handle empty response (typical for DELETE)
        result = resultProcessingFunction(null);
      }

      return {
        success: true,
        result,
      };
    }

    responseBody = await parseResponseBody(response);
    const errorCode = typeof responseBody?.error === "string" ? responseBody.error : undefined;

    return {
      success: false,
      message: getErrorMessage(response.status, errorCode),
      result: undefined,
    };
  } catch (e) {
    const error = e instanceof Error ? e : new Error(String(e));

    if (error.message !== REFRESH_UNAVAILABLE_MESSAGE) {
      sendToSentry(error, {
        url,
        method,
        options: redactOptionsForSentry({ method, body }),
        response,
        responseBody,
        additionalContext: {
          component: "fetchUsingApi",
          processingType: typeof resultProcessingFunction,
          phase: response ? "response-processing" : "request",
        },
      });
    }

    // No response: network failure or expired session. A response we could not process: generic error.
    const message =
      error.message === AUTH_FAILED_MESSAGE
        ? getErrorMessage(401)
        : response
          ? getErrorMessage(500)
          : getNetworkErrorMessage();

    return {
      success: false,
      message,
      result: undefined,
    };
  }
};

export const fetchWithAuth = async (url: string, options: FetchWithAuthOptions = {}): Promise<Response> => {
  return Sentry.startSpan(
    {
      name: `Auth ${options.method || "GET"} ${new URL(url).pathname}`,
      op: "http.client.auth",
    },
    async (span) => {
      const accessToken = getToken();
      let response: Response | undefined;

      try {
        const fetchOptions: CustomRequestInit = {
          ...options,
          credentials: "include",
          body: protectSensitiveJsonRequestBody(options.body),
          headers: {
            ...(options.headers || {}),
            ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
          },
        };

        span?.setAttributes(createSpanAttributes(url, options.method || "GET"));

        response = await fetchWithRetry(url, fetchOptions);

        // Only 401 means the access token is missing or stale. Other 4xx are business errors: no refresh, no replay.
        if (isClientError(response.status)) {
          const customHandler = options.errorHandlers?.[response.status];
          if (customHandler) {
            const responseBody = await parseResponseBody(response);
            customHandler(response, responseBody);
          } else if (response.status === 401) {
            response = await handleAuthError(url, fetchOptions, span);
          }
        }

        if (span) {
          updateSpanWithResponse(span, response);
        }

        return response;
      } catch (error) {
        const err = error instanceof Error ? error : new Error(String(error));

        // Skip sending standard auth errors to Sentry
        if (!isSessionOutcome(err)) {
          sendToSentry(err, {
            url,
            method: options.method,
            options: redactOptionsForSentry(options),
            additionalContext: {
              component: "fetchWithAuth",
              accessTokenPresent: Boolean(accessToken),
              requestContext: options.requestContext,
            },
          });
        }

        throw err;
      }
    },
  );
};

async function handleAuthError(
  url: string,
  fetchOptions: CustomRequestInit,
  parentSpan?: Sentry.Span,
): Promise<Response> {
  return Sentry.startSpan(
    {
      name: "Handle Auth Error",
      op: "http.client.auth.refresh",
      parentSpan: parentSpan,
    },
    async (span) => {
      try {
        const refreshResult = await refreshToken();

        if (refreshResult === "ok") {
          const newAccessToken = getToken();
          if (!newAccessToken) {
            throw new Error("Token refresh succeeded but no new token found");
          }

          const newResponse = await fetchWithRetry(url, {
            ...fetchOptions,
            headers: {
              ...(fetchOptions.headers || {}),
              Authorization: `Bearer ${newAccessToken}`,
            },
          });

          if (span) {
            updateSpanWithResponse(span, newResponse);
          }

          return newResponse;
        }

        // Server down or offline: the session may still be valid, so keep the token.
        if (refreshResult === "unavailable") {
          throw new Error(REFRESH_UNAVAILABLE_MESSAGE);
        }

        // Rejected refresh: the session is over.
        removeToken();

        // Get current path from router
        const currentLocation = routerInstance?.state.location;

        // Only redirect if user is not on a public route; come back here after login
        if (currentLocation?.pathname && !PUBLIC_ROUTES.some((route) => currentLocation.pathname.startsWith(route))) {
          routerInstance?.navigate(loginPathFor(currentLocation));
        }

        throw new Error(AUTH_FAILED_MESSAGE);
      } catch (error) {
        const err = error instanceof Error ? error : new Error(String(error));

        if (!isSessionOutcome(err)) {
          sendToSentry(err, {
            url,
            method: fetchOptions.method,
            options: redactOptionsForSentry(fetchOptions),
            additionalContext: {
              component: "handleAuthError",
              phase: "token-refresh",
            },
          });
        }

        throw err;
      }
    },
  );
}

export const refreshToken = async (): Promise<RefreshResult> => {
  if (refreshTokenPromise) {
    return refreshTokenPromise;
  }

  refreshTokenPromise = performRefreshToken();

  try {
    return await refreshTokenPromise;
  } finally {
    refreshTokenPromise = null;
  }
};

const performRefreshToken = async (): Promise<RefreshResult> => {
  return Sentry.startSpan(
    {
      name: "Refresh Token Request",
      op: "http.client.auth.refresh",
    },
    async (span) => {
      try {
        // Refreshing twice is harmless, so the POST may retry 5xx and network errors.
        const response = await fetchWithRetry(`${apiBase}/auth/refresh`, {
          method: "POST",
          credentials: "include",
          retry: true,
        });

        if (span) {
          updateSpanWithResponse(span, response);
        }

        if (!response.ok) {
          return refreshResultForStatus(response.status);
        }

        const data = (await response.json()) as AuthResponse;
        if (!data.accessToken) {
          return "unavailable";
        }
        setToken(data.accessToken);
        return "ok";
      } catch {
        return "unavailable";
      }
    },
  );
};
